import { Link } from 'react-router-dom';
import { Users, Fuel, Settings2, ArrowRight, Check, ShieldCheck, Car } from 'lucide-react';
import { Seo } from '@/components/Seo';
import { Container } from '@/components/ui/Container';
import { SectionHeading } from '@/components/ui/SectionHeading';
import { Photo } from '@/components/ui/Photo';
import { HIRE_POLICY } from '@/data/fleet';
import { useFleet } from '@/lib/useFleet';
import { formatPula } from '@/lib/money';
import { useImages } from '@/lib/images';
import { useReveal } from '@/lib/useReveal';

export function Cars() {
  useReveal();
  const img = useImages();
  const { active: fleet } = useFleet();

  return (
    <>
      <Seo title="Car Hire" description="Grace Haven Car Hire — reliable, well-kept vehicles in Gaborone. Compact hatchbacks, executive sedans, and 4x4 SUVs. Book by the day." />

      {/* Hero */}
      <section className="relative flex min-h-[60vh] items-center overflow-hidden bg-cream pt-24">
        <Photo src={img.carPillar} alt="A car from the Grace Haven fleet" className="absolute inset-0" imgClassName="opacity-80" />
        <div className="absolute inset-0 bg-gradient-to-r from-cream via-cream/85 to-transparent" />
        <Container className="relative z-10">
          <p className="eyebrow mb-4">Grace Haven Car Hire</p>
          <h1 className="max-w-xl font-display text-5xl font-medium leading-tight text-espresso sm:text-6xl">Drive your own way.</h1>
          <div className="rule-gold mt-6" />
          <p className="mt-6 max-w-lg font-body font-light leading-relaxed text-muted">
            Reliable, well-kept vehicles for business or leisure — hired by the day, right here in Gaborone.
          </p>
          <Link to="/cars/book" className="btn-primary mt-8">Hire a car <ArrowRight size={16} /></Link>
        </Container>
      </section>

      {/* Fleet */}
      <section className="bg-cream py-24">
        <Container>
          <SectionHeading eyebrow="Our fleet" title="Choose your vehicle" center intro="Rates per day in Botswana Pula. Insurance included; a refundable deposit applies." />
          <div className="mt-16 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {fleet.map((v) => (
              <article key={v.id} className="reveal flex flex-col border border-line bg-paper">
                <Photo src={v.image || img.carPillar} alt={v.name} className="aspect-[4/3] w-full" />
                <div className="flex flex-1 flex-col p-6">
                  <p className="font-body text-[11px] uppercase tracking-wide text-gold-deep">{v.category}</p>
                  <h3 className="mt-1 font-display text-2xl text-espresso">{v.name}</h3>
                  <div className="rule-gold mt-3" />
                  {v.blurb && <p className="mt-4 flex-1 font-body text-sm leading-relaxed text-muted">{v.blurb}</p>}
                  <ul className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 font-body text-xs text-muted">
                    {v.seats ? <li className="flex items-center gap-1.5"><Users size={13} className="text-gold-deep" /> {v.seats} seats</li> : null}
                    {v.transmission ? <li className="flex items-center gap-1.5"><Settings2 size={13} className="text-gold-deep" /> {v.transmission}</li> : null}
                    {v.fuel ? <li className="flex items-center gap-1.5"><Fuel size={13} className="text-gold-deep" /> {v.fuel}</li> : null}
                  </ul>
                  <div className="mt-6 flex items-center justify-between border-t border-line pt-4">
                    <span className="font-display text-2xl text-espresso">{formatPula(v.pricePerDay)} <span className="font-body text-xs text-muted">/ day</span></span>
                    <Link to={`/cars/book?car=${v.id}`} className="btn-primary !px-4 !py-2">Book <ArrowRight size={14} /></Link>
                  </div>
                </div>
              </article>
            ))}
            {fleet.length === 0 && (
              <p className="col-span-full py-10 text-center font-body text-muted">Our fleet is being updated — please check back soon or contact us.</p>
            )}
          </div>
        </Container>
      </section>

      {/* Good to know */}
      <section className="bg-paper py-24">
        <Container>
          <SectionHeading eyebrow="Good to know" title="Hire terms" center intro="A few details to help you plan. Anything else, just ask." />
          <div className="mx-auto mt-10 grid max-w-4xl gap-6 lg:grid-cols-[260px_1fr]">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-1">
              <div className="border border-line bg-cream p-5 text-center lg:text-left">
                <ShieldCheck size={18} className="mx-auto text-gold-deep lg:mx-0" />
                <p className="mt-2 font-body text-[11px] uppercase tracking-wide text-muted">Insurance</p>
                <p className="font-display text-xl text-espresso">Included</p>
              </div>
              <div className="border border-line bg-cream p-5 text-center lg:text-left">
                <Car size={18} className="mx-auto text-gold-deep lg:mx-0" />
                <p className="mt-2 font-body text-[11px] uppercase tracking-wide text-muted">Min. driver age</p>
                <p className="font-display text-xl text-espresso">{HIRE_POLICY.minAge}</p>
              </div>
            </div>
            <ul className="grid gap-3 sm:grid-cols-2">
              {HIRE_POLICY.conditions.map((c) => (
                <li key={c} className="flex items-start gap-2.5 font-body text-sm leading-relaxed text-muted">
                  <Check size={16} className="mt-0.5 shrink-0 text-gold-deep" /> {c}
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-12 text-center">
            <Link to="/cars/book" className="btn-primary">Check availability &amp; book <ArrowRight size={16} /></Link>
          </div>
        </Container>
      </section>
    </>
  );
}
